import { TEducationLevel } from '@/common/constants/education';
import { IMenhirBenefitSlug } from '@/common/integrations/menhir/employee/benefits/benefits.types';
import {
  IEstablishment,
  IMenhirEstablishment,
} from '@/common/integrations/menhir/employee/establishments/establishments.types';
import {
  IAppleOrderItem,
  IMenhirAppleOrderItem,
} from '@/common/integrations/menhir/employee/marketplace/marketplace.types';
import { IMenhirMovement } from '@/common/integrations/menhir/employee/movements/movements.types';
import {
  IMenhirReimbursableItem,
  IReimbursableItem,
} from '@/common/integrations/menhir/employee/reimbursements/reimbursements.types';
import {
  TDynamicFormField,
  TMenhirDynamicFormField,
} from '@/common/integrations/menhir/forms/forms.types';
import {
  ICatalogPolicy,
  ICatalogProduct,
  ICatalogProductWithProvider,
  IReimbursableBenefit,
} from '@/common/types/catalog';
import { IAddress } from '@/common/types/general';
import {
  IMenhirCatalogOrderWithPolicy,
  IMenhirCatalogPolicy,
  IMenhirCatalogProduct,
  IMenhirInsuranceOrder,
} from '@/common/types/menhir/catalog';
import {
  IMenhirAddress,
  IMenhirFile,
  IMenhirPaginationResponse,
} from '@/common/types/menhir/general';
import { IFile, TMoney } from '@coverflex-tech/hypernova-utils';
import { MenhirBudgetLogos } from '../budgets/budgets.types';

export type TMenhirRetirementInvestmentPendingOrder = {
  id: string;
  amount: TMoney;
  status: 'pending' | 'failed' | 'completed';
  reference: string;
  inserted_at: string;
  type: 'setup' | 'manual_topup' | 'recurrence';
  product_name: string;
};

export type TRetirementInvestmentPendingOrder = {
  id: string;
  amount: TMoney;
  status: 'pending' | 'failed' | 'completed';
  reference: string;
  insertedAt: string;
  type: 'setup' | 'manual_topup' | 'recurrence';
  productName: string;
};

export type TOrderCategorySlug =
  | 'retirement-investment'
  | 'technology'
  | 'transportation'
  | 'professional-training'
  | 'health-wellbeing'
  | 'gym-fitness'
  | 'education-training'
  | 'senior-expenses'
  | 'childcare-voucher'
  | 'meals'
  | 'health'
  | 'parking'
  | 'summer-camp'
  | 'donation'
  | 'other-benefits'
  | 'budgets'
  | 'cashout'
  | 'fringe'
  | 'recreation'
  | 'family-care-services'
  | 'it-education'
  | 'it-transportation'
  | 'pension-funds'
  | 'es-public-transport'
  | 'es-childcare'
  | 'es-professional-development';

export interface IMenhirOrdersList<T extends TOrderCategorySlug> {
  orders: IMenhirPaginationResponse<IMenhirOrder<T>>;
}

export interface IMenhirOrderEnvelope<T extends TOrderCategorySlug> {
  order: IMenhirOrder<T>;
}

export interface IMenhirOrderPolicyEnvelope {
  policy: IMenhirCatalogPolicy;
}

export interface IMenhirOrder<
  T extends TOrderCategorySlug = TOrderCategorySlug,
> {
  id: string;
  is_closed: boolean;
  product: IMenhirCatalogProduct<T>;
  amount: TMoney;
  reference: string;
  files?: IMenhirFile[];
  status: CategorySlugOrderStatusMapping[T];
  movements?: IMenhirMovement[];
  operation?: IMenhirOrderOperation;
  recurrence?: {
    unit: 'month';
    period: number;
    next_charge_at?: string;
  };
  inserted_at?: string;
}
export interface IMenhirOrderOperation {
  id: string;
  operation: 'setup' | 'manual_topup' | 'recurrence';
  status: string;
}

export interface ICreatePolicyTopUpParams<T extends TOrderCategorySlug> {
  category: T;
  policyId: string;
  amount: TMoney;
  confirmInvestmentTopUp?: boolean;
}

export interface ICreateOrderParams<T extends TOrderCategorySlug> {
  category: T;
  productId: string;
  amount: TMoney;
  recurrent: boolean;
}

export interface IUpdateOrderParams<T extends TOrderCategorySlug> {
  category: T;
  orderId: string;
  productId?: string;
  amount?: TMoney;
  recurrent?: boolean;
}

export interface IDeleteOrderParams<T extends TOrderCategorySlug> {
  category: T;
  orderId: string;
}

export interface ICancelReimbursementOrderParams<T extends TOrderCategorySlug> {
  category: T;
  orderId: string;
}

export interface IGetOrdersParams<T extends TOrderCategorySlug> {
  category: T;
}

export interface IGetOrderParams<T extends TOrderCategorySlug> {
  category: T;
  orderId: string;
}

export interface IOrder<T extends TOrderCategorySlug = TOrderCategorySlug> {
  id: string;
  isClosed: boolean;
  product: ICatalogProduct<T>;
  amount: TMoney;
  status: CategorySlugOrderStatusMapping[T];
  files: {
    name: string;
    slug: string;
    url: string;
  }[];
  reference: string;
  operation?: IOrderOperation;
  recurrence?: {
    unit: 'month';
    period: number;
    next_charge_at?: string;
  };
  insertedAt?: string;
}

export interface IOrderWithPolicy<T extends TOrderCategorySlug>
  extends IOrder<T> {
  policy: ICatalogPolicy | null;
}

export interface IRetirementInvestmentOrder
  extends IOrderWithPolicy<'retirement-investment'> {
  product: ICatalogProductWithProvider<'retirement-investment'>;
}

export const isAppleOrder = (
  order: IMenhirOrder<TOrderCategorySlug>
): order is IMenhirAppleOrder => order.product.slug === 'apple';

export const isInsuranceOrder = (
  order: IMenhirOrder<TOrderCategorySlug>
): order is IMenhirInsuranceOrder => order.product.category.slug === 'health';

export const isDonationOrder = (
  order: IMenhirOrder<TOrderCategorySlug>
): order is IMenhirDonationOrder => order.product.category.slug === 'donation';

export const isOrderWithPolicy = (
  order: Pick<IMenhirOrder<TOrderCategorySlug>, 'product'>
): order is IMenhirCatalogOrderWithPolicy<TOrderCategorySlug> =>
  'policy' in order;

export interface IOrderOperation {
  id: string;
  type: 'setup' | 'manual_topup' | 'recurrence';
  status: string;
}
export interface IMenhirDonationOrder extends IMenhirOrder<'donation'> {
  establishment: IMenhirEstablishment;
}

export interface IMenhirDonationOrderEnvelope {
  order: IMenhirDonationOrder;
}
export interface IDonationOrder extends IOrder<'donation'> {
  establishment: IEstablishment;
}

export interface ICreateDonationOrderParams {
  establishmentId: string;
  amount: TMoney;
}
export interface ICreateReimbursementOrderParams<T extends TOrderCategorySlug> {
  category: T;
  amount: TMoney;
  vatAmount?: TMoney;
  purchasedAt: Date;
  files: IFile[];
  reimbursableItem?: IReimbursableItem;
  budgetEmployeeId?: string;
  description?: string;
  invoiceNumber?: string;
  fiscalYear?: number;
  beneficiaryPersonId?: string;
  education?: TEducationLevel;
  confirmReimbursementCreation?: boolean;
  customFields?: Record<string, any>[];
  supportFiles?: IFile[];
  // TODO: Remove this when cleaning FF_REIMBURSEMENTS_MULTIPLE_FILES_UPLOAD
  asFormData?: boolean;
}

export interface IGetReimbursementDetailsParams<T extends TOrderCategorySlug> {
  category: T;
  orderId: string;
}

export interface IUploadReimbursementFilesParams<T extends TOrderCategorySlug> {
  category: T;
  files: IFile[];
}

export interface IMenhirReimbursementOrderDetailsEnvelope<
  T extends TOrderCategorySlug,
> {
  order: IMenhirReimbursementOrderDetails<T>;
}

export interface IReimbursementOrderDetails<T extends TOrderCategorySlug>
  extends Omit<IOrder<T>, 'movements' | 'files'> {
  vatAmount?: TMoney;
  files: IFile[];
  receiptDate: string;
  iban: string;
  statusDetails?: {
    date?: string;
    reason: string;
    updatedBy: 'company' | 'coverflex';
  };
  transportationCard?: {
    number: string;
    expirationDate: string;
  };
  reimbursableItem?: IReimbursableItem;
  budgetEmployee?: {
    budget: {
      logo: MenhirBudgetLogos;
      name: string;
    };
    id: string;
  };
  description?: string;
  customFields?: Array<
    Pick<TDynamicFormField, 'id' | 'label' | 'value' | 'inputType'>
  >;
  supportFiles?: IFile[];
}

export interface CategorySlugOrderStatusMapping
  extends Record<TOrderCategorySlug, string> {
  'retirement-investment':
    | 'created'
    | 'pending_transfer'
    | 'pending_approval'
    | 'pending_contract'
    | 'approved'
    | 'issued'
    | 'failed'
    | 'rejected';
  technology:
    | 'created'
    | 'pending_transfer'
    | 'pending_approval'
    | 'confirming_approval'
    | 'completed'
    | 'approved'
    | 'cancelled'
    | 'rejected';
  'professional-training':
    | 'created'
    | 'pending_transfer'
    | 'pending_approval'
    | 'completed'
    | 'cancelled'
    | 'rejected';
  transportation:
    | 'created'
    | 'pending_transfer'
    | 'pending_approval'
    | 'approved'
    | 'completed'
    | 'cancelled'
    | 'rejected';
  insurance: 'approved' | 'pending_transfer' | 'pending_approval' | 'cancelled';
  parking:
    | 'created'
    | 'pending_transfer'
    | 'pending_approval'
    | 'approved'
    | 'completed'
    | 'cancelled'
    | 'rejected';
  'summer-camp':
    | 'created'
    | 'pending_transfer'
    | 'pending_approval'
    | 'approved'
    | 'completed'
    | 'cancelled'
    | 'rejected';
}

export interface IMenhirReimbursementOrderDetails<T extends TOrderCategorySlug>
  extends IMenhirOrder<T> {
  vat_amount?: TMoney;
  files: IMenhirFile[];
  purchased_at: string;
  status_details?: {
    at: string;
    reason: string;
    by: 'company' | 'coverflex';
  };
  bank_account: {
    number: string;
  };
  budget_employee?: {
    budget: {
      logo: MenhirBudgetLogos;
      name: string;
    };
    id: string;
  };
  description?: string;
  transportation_card?: {
    number: string;
    expires_at: string;
  };
  reimbursable_item?: IMenhirReimbursableItem;
  custom_fields?: Array<
    Pick<TMenhirDynamicFormField, 'id' | 'label' | 'value' | 'input_type'>
  >;
  support_files?: IMenhirFile[];
}

export interface IMenhirAppleOrder extends IMenhirOrder<'technology'> {
  marketplace_order: {
    id: string;
    total: TMoney;
    items: IMenhirAppleOrderItem[];
    shipping_address: IMenhirAddress;
  };
  status_details?: {
    at?: string;
    reason: string;
  };
  price_adjustment_amount?: TMoney;
}

export interface IAppleOrder extends IOrder<'technology'> {
  marketplaceOrder: {
    id: string;
    total: TMoney;
    items: IAppleOrderItem[];
    shippingAddress: IAddress;
  };
  statusDetails?: {
    at?: Date;
    reason: string;
  };
  priceAdjustmentAmount: TMoney;
}

export type TMenhirGenericPendingOrder = {
  amount: TMoney;
  id: string;
  inserted_at: string;
  reference: string;
  status: string;
  type: 'reimbursement' | 'apple_marketplace';
  category: IMenhirBenefitSlug;
};

export type TGenericPendingOrder = {
  amount: TMoney;
  id: string;
  insertedAt: string;
  reference: string;
  status: string;
  type: 'apple_marketplace' | 'reimbursement';
  category: IMenhirBenefitSlug;
};

export type TApplePendingOrder = TGenericPendingOrder & {
  type: 'apple_marketplace';
};

export type TReimbursementPendingOrder = TGenericPendingOrder & {
  type: 'reimbursement';
  category: IReimbursableBenefit;
};
